import { useEffect, useState } from "react"
import { useBoolean, useNumber, useString } from "helpers/hooks"
import {
  getFileIntegrityAndSignatureCheckComponentHistoryMiddleware,
  revertMergedVersionMiddleware,
} from "pages/project-component-detail/services"
import Button from "components/Button/Button"
import { delay } from "helpers/utils"
import HashLoader from "react-spinners/HashLoader"
import { API_URL } from "config/environments"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { encryptionHelper } from "helpers/encryption"
import { getAccessTokenInLocalStorage } from "helpers/access_token"

interface Props {
  isCallAPI: boolean
  projectKey: string
  projectComponentHistory: string
  projectComponentHistoryRevert?: string
  label?: string
  checkFileSucess: () => void
  checkFileError: (newFileErrors: { name: string; error: string }[]) => void
  onCloseModal?: () => void
}

export const FileIntegrityAndSignatureCheckComponentHistory = (
  props: Props
) => {
  const {
    isCallAPI,
    checkFileSucess,
    checkFileError,
    projectComponentHistory,
    projectKey,
    projectComponentHistoryRevert,
    onCloseModal,
    label,
  } = props
  const name = useString("")
  const fileTotal = useNumber(0)
  const fileIndex = useNumber(0)
  const fileName = useString("")
  const isLoading = useBoolean(false)
  const [fileErrors, setFileErrors] = useState<
    { name: string; error: string }[]
  >([])
  const isRollingBack = useBoolean(false)

  useEffect(() => {
    if (!isCallAPI) {
      return
    }
    getDataHistoryFile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallAPI])

  const parseMetadata = (
    headerValue: string | null
  ): {
    mimeType: string
    encryptVersion: string
  } => {
    if (!headerValue) return { mimeType: "", encryptVersion: "" }

    try {
      const parsed = JSON.parse(headerValue)
      return {
        mimeType: parsed["mime-type"] ?? "",
        encryptVersion: parsed["encrypt-version"] ?? "",
      }
    } catch {
      return { mimeType: "", encryptVersion: "" }
    }
  }

  const getDataHistoryFile = async () => {
    try {
      isLoading.setValue(true)
      const dataFiles =
        await getFileIntegrityAndSignatureCheckComponentHistoryMiddleware(
          projectComponentHistory
        )
      isLoading.setValue(false)
      name.setValue(dataFiles.name)
      let completedSteps = 0
      const files: {
        file: string
        keyToFolder: string
      }[] = dataFiles.data || []
      if (!files.length) {
        checkFileError([])
        return
      }
      fileTotal.setValue(files.length)
      const token = getAccessTokenInLocalStorage()
      const fileErrors: {
        name: string
        error: string
      }[] = []
      for (const elFile of files) {
        const { file, keyToFolder } = elFile
        const getLastSegment = (path) =>
          path.split("/").filter(Boolean).pop() || path.trim()

        let extractedFileName = file

        if (keyToFolder) {
          const fileLower = file.toLowerCase()
          const keyLower = keyToFolder.toLowerCase()
          const idx = fileLower.indexOf(keyLower)

          extractedFileName =
            idx !== -1
              ? getLastSegment(file.slice(idx + keyToFolder.length))
              : getLastSegment(file)
        } else {
          extractedFileName = getLastSegment(file)
        }

        completedSteps++
        fileIndex.setValue(completedSteps)
        fileName.setValue(extractedFileName)
        const fileURL = `${API_URL}/api/assets/${encodeURIComponent(file)}`
        const [fileResponse, hashKeyResponse] = await Promise.all([
          fetch(fileURL, { headers: { Authorization: token } }),
          fetch(
            `${API_URL}/api/file/hash?file_key=${encodeURIComponent(file)}`,
            {
              headers: {
                Authorization: token,
                "Content-Type": "application/json",
              },
            }
          ),
        ])
        const metadata = parseMetadata(fileResponse.headers.get("x-meta-data"))
        const { hash_key, public_key } = await hashKeyResponse.json()
        if (metadata.encryptVersion) {
          console.log("------- DECRYPT FILE-----------")
          if (!fileResponse.body) {
            fileErrors.push({
              name: extractedFileName,
              error: "File not found",
            })
            continue
          }
          const decryptKey = await encryptionGCMHelper.decryptKeyByCrypto(
            projectKey
          )
          let decryptedStream: any = encryptionGCMHelper.decryptGCMStream(
            fileResponse.body,
            decryptKey
          )
          const blobGCM = await new Response(decryptedStream, {
            headers: {
              "Content-Type": metadata.mimeType,
            },
          }).blob()
          decryptedStream = null // Clear memory
          if (blobGCM.size === 0) {
            fileErrors.push({
              name: extractedFileName,
              error: "Decrypted file is empty",
            })
            continue
          }
          if (hash_key && public_key) {
            const publicCryptoKey =
              await encryptionGCMHelper.coverPublicKeyToCryptoKey(public_key)
            if (!publicCryptoKey) {
              fileErrors.push({
                name: extractedFileName,
                error: "Invalid public key",
              })
              continue
            }
            const hashBuffer = await encryptionGCMHelper.calculateSHA256(
              blobGCM
            )
            const signatureBuffer =
              encryptionGCMHelper.base64ToArrayBuffer(hash_key)
            const verifiedGCM = await crypto.subtle.verify(
              { name: "RSA-PSS", saltLength: 32 },
              publicCryptoKey,
              signatureBuffer,
              hashBuffer
            )
            console.log("Signature", verifiedGCM ? "//SUCCESS//" : "//FAILED//")
            if (!verifiedGCM) {
              fileErrors.push({
                name: extractedFileName,
                error: "Signature verification failed",
              })
              continue
            }
          }
        } else {
          let temp = (await fileResponse.text()) as any
          let dataDecrypt: any = await encryptionGCMHelper.decryptContext(
            projectKey,
            temp,
            true
          )
          const contentType =
            fileResponse.headers.get("content-type") ||
            "application/octet-stream"
          const blobCBC = new Blob([dataDecrypt], { type: contentType })
          dataDecrypt = null // Clear memory
          if (blobCBC.size === 0) {
            fileErrors.push({
              name: extractedFileName,
              error: "Decrypted file is empty",
            })
            continue
          }
          let blobText: any = await blobCBC.text()
          if (hash_key && public_key) {
            const verifiedCBC = encryptionHelper.checkVerifiedSignature(
              public_key,
              hash_key,
              blobText
            )
            blobText = null // Clear memory
            console.log("Signature", verifiedCBC ? "//SUCCESS//" : "//FAILED//")
            if (!verifiedCBC) {
              fileErrors.push({
                name: extractedFileName,
                error: "Signature verification failed",
              })
              continue
            }
          }
        }
      }
      if (fileErrors.length) {
        if (projectComponentHistoryRevert) {
          setFileErrors(fileErrors)
          try {
            await revertMergedVersionMiddleware(
              projectComponentHistoryRevert,
              projectComponentHistory
            )
            await delay(2000)
            isRollingBack.setValue(false)
          } catch (error) {}
          checkFileError(fileErrors)
        } else {
          checkFileError(fileErrors)
        }
      } else {
        checkFileSucess()
      }
    } catch (error) {
      console.log(error)
      checkFileError([])
    }
  }

  const fileProgress = () => {
    return fileIndex.value && fileTotal.value
      ? (fileIndex.value / fileTotal.value) * 100
      : 0
  }
  if (isLoading.value) {
    return (
      <div className="flex flex-col h-[68px] items-center justify-center">
        <HashLoader color="#F7AC1B" loading={true} size={50} />
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full">
      <p className="text-[14px] text-[#111111] leading-[22px] font-semibold mb-[8px]">
        {`${label ? label : projectComponentHistoryRevert ? "Master -" : ""}${
          name.value
        }`}
      </p>
      <div className="flex items-center justify-between mb-[8px] pr-4">
        <p className="text-[13px] text-[#111111] leading-[22px] truncate max-w-[90%]">
          {fileName.value}
        </p>
        <p className="text-[13px] text-[#111111] leading-[22px] flex-shrink-0 ml-2">
          {fileIndex.value} / {fileTotal.value}
        </p>
      </div>
      <div className="flex items-center pr-4">
        <div className="bg-[#E4E4E4] rounded-full h-2 w-full">
          <div
            className={`h-2 rounded-full transition-all duration-500 ease-out`}
            style={{
              width: `${fileProgress()}%`,
              backgroundColor: "#F7AC1B",
            }}
          ></div>
        </div>
      </div>
      {fileErrors.length > 0 && (
        <div className="flex flex-col mt-4">
          <p className="text-[14px] text-[#E74C3C] leading-[22px] font-semibold mb-3">
            {fileErrors.length} file error{fileErrors.length !== 1 ? "s" : ""}{" "}
            found:
          </p>
          <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto">
            {fileErrors.map((error, index) => (
              <div
                key={index}
                className="flex flex-col p-[5px] bg-[#FFF5F5] border border-[#FECACA] rounded-lg mr-4"
              >
                <p className="text-[13px] text-[#111111] leading-[20px] font-medium mb-1 truncate">
                  {error.name}
                </p>
                <p className="text-[12px] text-[#E74C3C] leading-[18px]">
                  {error.error}
                </p>
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#111111] leading-[22px] font-semibold text-center mt-[15px] pr-4">
            {isRollingBack.value
              ? `Rollback in progress…`
              : "Rollback completed."}
          </p>
          {!isRollingBack.value ? (
            <div className="flex w-full items-center justify-center mt-[15px] pr-4">
              <Button
                title="Close"
                widthBtn="100%"
                colorBtn="white"
                onClick={onCloseModal}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
